/**
 * Focus Ask create action dispatcher.
 *
 * The workspace `create-asks` skill always emits a single `MessageAction`
 * whose payload is `{ asks: FocusAskCreatePayload[] }` — one entry for the
 * single-ask case, many entries for the bulk case. The dispatcher reads
 * `data.asks` off the (single) inbound action and routes by array length:
 *
 *   - `asks.length === 1` → {@link FocusAskCreateRenderer} (inline preview)
 *   - `asks.length > 1`  → {@link BulkFocusAskCreateAction} (row card)
 *
 * In both paths the confirmed payload sent back to the platform preserves the
 * `{ asks: [...] }` wrapper so the BE receives the same shape it emitted, just
 * with whichever asks the user kept and any in-place edits merged in.
 */
import React, { useCallback, useMemo } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { type ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import { FocusAskCreateRenderer } from './ask-create-suggestion-preview';
import { BulkFocusAskCreateAction } from './bulk-ask-create/BulkFocusAskCreateAction';
import { extractAsks } from './extract-asks';
import { mergeFocusAskFormIntoPayload } from './merge-focus-ask-form-into-payload';
import { normalizeFocusAskPayload } from './normalize-focus-ask-payload';
import type { FocusAskFormData } from './types';

type FocusAskAction = {
	invocationId: string;
	status?: string;
	data: unknown;
};

type FocusAskRendererAction = {
	invocationId: string;
	status?: string;
	data: FocusAskFormData;
};

type FocusAskCreateActionProps = Omit<
	ActionRendererFor<typeof ActionKeys.FocusCreateAsk>,
	'orgId'
> & {
	orgId: string;
};

const SingleFocusAskCreateAction = (props: FocusAskCreateActionProps): React.JSX.Element | null => {
	const {
		actions,
		cloudId,
		orgId,
		getReadonly,
		getStatus,
		onCustomDataConfirm,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
	} = props;
	const action = actions[0];
	const asks = useMemo(() => extractAsks(action?.data), [action]);
	const singleAsk = asks[0];

	const normalized = useMemo(() => normalizeFocusAskPayload(singleAsk), [singleAsk]);

	const actionForRenderer: FocusAskRendererAction = useMemo(
		() =>
			action
				? {
						invocationId: action.invocationId,
						status: action.status,
						data: normalized,
					}
				: {
						invocationId: '',
						data: normalized,
					},
		[action, normalized],
	);

	const handleConfirm = useCallback(
		async (_action: FocusAskAction, formData: FocusAskFormData) => {
			if (!action) {
				return;
			}
			const data = {
				asks: [mergeFocusAskFormIntoPayload(singleAsk, formData)],
			};

			if (
				FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
			) {
				const idleActions = actions.filter((action) => getStatus(action) === 'idle');
				await sendMessageConfirmedTools({
					agentId: recipientAgentId,
					conversationId,
					messageSources: {
						interactionSource: 'focusAskCreateAction',
					},
					confirmed_tools: {
						message_id: messageId,
						tools: idleActions.map((idleAction) =>
							idleAction.invocationId === action.invocationId
								? buildToolFromAction(idleAction, 'confirmed', data)
								: buildToolFromAction(idleAction, 'cancelled'),
						),
					},
				});
				return;
			}
			await onCustomDataConfirm(action, data);
		},
		[
			action,
			singleAsk,
			onCustomDataConfirm,
			sendMessageConfirmedTools,
			recipientAgentId,
			conversationId,
			messageId,
			actions,
			getStatus,
		],
	);

	if (!action) {
		return null;
	}

	const readonly = getReadonly(action);
	const status = getStatus(action);
	const isSubmitting = status === 'started';
	const isFinished = status === 'finished' || action.status === 'finished';

	return (
		<FocusAskCreateRenderer
			action={actionForRenderer}
			cloudId={cloudId}
			orgId={orgId}
			readonly={readonly}
			isActionFinished={isFinished}
			isSubmitting={isSubmitting}
			onConfirm={handleConfirm}
		/>
	);
};

export const FocusAskCreateAction = (
	props: FocusAskCreateActionProps,
): React.JSX.Element | null => {
	const { actions } = props;
	const action = actions[0];
	const asks = useMemo(() => extractAsks(action?.data), [action]);

	if (!action || asks.length === 0) {
		return null;
	}

	if (asks.length === 1) {
		return <SingleFocusAskCreateAction {...props} />;
	}

	return <BulkFocusAskCreateAction {...props} />;
};
